'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Search, Heart, ShoppingBag } from 'lucide-react'
import { useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import MobileSearchBar from './MobileSearchBar'
import { useRouter } from 'next/navigation'
import useCartStore from '@/store/cartStore'
import useWishListStore from '@/store/wishListStore'
import useCommonStore, { masterLanguage } from '@/store/commonStore'
import { toast } from '@/hooks/use-toast'
import Loader from '@/app/loading'
import { GTMLanguageSwitch } from '@/lib/analytics'


interface MobileHeaderProps {
    showBagItems: boolean;
    categories: ICategory[];
    showWishListItems: boolean
}
interface ICategory {
    label: string;
    url: string;
    path: string;
}
export function MobileHeader({ categories, showBagItems, showWishListItems }: MobileHeaderProps) {
    const router = useRouter()
    const [addressModalOpen, setAddressModalOpen] = useState(false)
    const [addAddressOpen, setAddAddressOpen] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [loading, setLoading] = useState(false)
    const { wishList } = useWishListStore()
    const { cart } = useCartStore()
    const { user, isAuth, updateUserLanguageAndCurrency, language, translations } = useCommonStore()

    const languageToggle = async () => {
        setLoading(true)
        let language_to = language?.code === "EN" ? "AR" : "EN"
        GTMLanguageSwitch(language?.code, language_to)
        const result = await updateUserLanguageAndCurrency(language?.code === "EN" ? "AR" : "EN", language?.code === "EN" ? "AED" : "AED")
        if (result?.status) {
            router.refresh();
            setLoading(false)
        } else {
            setLoading(false)
            toast({
                title: "Error",
                description: result?.message || result?.error,
                variant: "destructive"
            })
        }
    }
    if (loading) {
        return (
            <Loader />
        )
    }

    return (
        <>
            <header className="bg-white md:hidden shadow-sm bg-[radial-gradient(100%_303.45%_at_100%_104.44%,_#FFF2E6_0%,_#FFFBF7_74.19%,_#FFE8D4_100%)] p-4 space-y-4">
                {/* Logo and Icons Row */}
                <div className="flex items-center justify-between border-b border-[#FBEBDD] pb-3">
                    <Link href="/" className="flex-shrink-0">
                        <Image
                            src="https://curlified.com/assets/logo.cf78a1b8.svg"
                            alt="Curlified"
                            width={120}
                            height={32}
                            className="h-8 w-auto"
                        />
                    </Link>

                    <div className="flex items-center gap-3">
                        {isAuth && <div className="relative" onClick={() => router.push('/profile?tab=wishlist')}>
                            <img
                                src={`${process.env.NEXT_PUBLIC_ICON_URL}9b871b36-dcdc-4f70-a107-7e9199484a38.svg`}
                                alt="wishlist"
                                className="w-6 h-6"
                            />
                            {showWishListItems && wishList?.length > 0 && (
                                <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full px-1 py-1">
                                    {/* {wishList?.length} */}
                                </span>
                            )}
                        </div>}
                        {/* <img src={`${process.env.NEXT_PUBLIC_ICON_URL}9b871b36-dcdc-4f70-a107-7e9199484a38.svg`} alt="" className="w-6 h-6"/> */}
                        <div className="relative" onClick={() => router.push('/mycart')}>
                            <img src={`${process.env.NEXT_PUBLIC_ICON_URL}3c17c865-3652-49af-9125-defed82e01db.svg`} alt="cart icon" className="w-6 h-6" />
                            {showBagItems && cart?.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[14px] h-[14px] flex items-center justify-center text-[10px]">
                                    {cart?.length}
                                </span>
                            )}
                        </div>
                        {/* Language Toggle */}
                        <button
                            className="flex items-center px-3 py-1 rounded-full bg-[#6D401A] text-white font-bold text-sm focus:outline-none min-w-[70px]"
                            onClick={() => languageToggle()}
                            aria-label="Toggle language"
                        >
                            <span className={`w-2 h-2 rounded-full mr-2 ${language?.code === "EN" ? "bg-white" : "hidden"}`}></span>
                            <span className={`${language?.code === "EN" ? "text-white" : "text-[#A67C52]"}`}>EN</span>
                            <span className="mx-1 text-[#fff]/60">/</span>
                            <span className={`${language?.code === "AR" ? "text-white" : "text-[#A67C52]"}`}>AR</span>
                            <span className={`w-2 h-2 rounded-full mr-2 ${language?.code === "AR" ? "bg-white" : "hidden"}`}></span>
                        </button>
                    </div>
                </div>
                {/* Location label */}
                <div
                    className="text-sm cursor-pointer "
                    onClick={() => {
                        if (isAuth) {
                            setAddressModalOpen(true);
                        } else {
                            router.push('/login');
                        }
                    }
                    }
                >
                    <span className="flex items-center gap-1 font-bold text-sm flex items-center">
                        <img src={`${process.env.NEXT_PUBLIC_ICON_URL}73310dcc-b06b-4ec8-a61d-331efd2f382a.svg`} alt="" className="w-5 mr-1" width={24} height={24} />
                        {user?.address?.address_name || translations["select_address"] || 'Select Address'}
                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" className="inline ml-1 rotate-90" viewBox="0 0 24 24" > <path d="M9 18l6-6-6-6" /> </svg>
                    </span>
                    <span className="truncate max-w-[90%]">
                        {user?.address?.locality ? user?.address?.locality + "," : ''} {user?.address?.address_line1 ? user?.address?.address_line1 + "," : ''} {user?.address?.zip_code ? user?.address?.zip_code : ''}
                    </span>
                </div>
                {/* Search Bar Row */}
                <div className="">
                    <div className="relative">
                        <input
                            type="search"
                            placeholder={translations["search"]}
                            className="w-full rounded-[12px] px-10 py-3 text-xs focus:outline-none border border-chart-2 bg-white"
                            onFocus={() => setShowSearch(true)}
                            readOnly
                        />
                        <Search
                            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 cursor-pointer"
                            onClick={() => setShowSearch(true)}
                        />
                    </div>
                </div>
                {/* Categories Row */}
                <div className="overflow-x-auto scroll-container -mx-4 px-4">
                    <div className="flex gap-6 w-max">
                        {categories.map((item, index: number) => (
                            <Link
                                key={index}
                                href={item.path}
                                className={`flex flex-row gap-2 items-center }`} //${index==0 ? 'border-b-4 border-primary' : ''
                            >
                                <Image
                                    src={item.url}
                                    alt={item.label}
                                    width={24}
                                    height={26}
                                    className="object-cover"
                                />
                                {item.label && <span className="text-[12px]">
                                    {item.label}
                                </span>}
                            </Link>
                        ))}
                    </div>
                </div>
            </header>
            {showSearch && (
                <MobileSearchBar showSearch={showSearch} onClose={() => setShowSearch(false)} />
            )}
            {addressModalOpen && (
                <AddressModal
                    open={addressModalOpen}
                    onClose={() => setAddressModalOpen(false)}
                />
            )}
            {addAddressOpen && (
                <AddAddress
                    translations={translations}
                    open={addAddressOpen}
                    onClose={() => setAddAddressOpen(false)}
                />
            )}
        </>
    )
}

const AddressModal = dynamic(
    () => import('./AddressModal').then((mod) => mod.AddressModal),
    { ssr: false }
)

const AddAddress = dynamic(
    () => import('./AddAddress').then((mod) => mod.AddAddress),
    { ssr: false }
)
